import { useState, useEffect, useRef } from 'react';
import { formatPhone } from '../components/formatPhone';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import { FaWrench, FaPlus, FaExclamationTriangle, FaClock, FaCheckCircle, FaTimes, FaUser, FaPhone, FaBuilding, FaChevronRight, FaCalendarAlt, FaTools, FaImages, FaTrash, FaKey, FaFilter, FaTag, FaTint, FaBolt, FaThermometerHalf, FaCog, FaBug, FaQuestionCircle, FaMinus, FaCircle } from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './WorkOrders.css';
import './Home.css';
import LoadingScreen from './LoadingScreen';
import Tutorial from './Tutorial';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

const STATUS_CLASS  = { 'Open': 'wo-st-open', 'In Progress': 'wo-st-inprog', 'Completed': 'wo-st-done' };
const PRIORITY_CLASS = { 'Urgent': 'wo-pri-high', 'Medium': 'wo-pri-med', 'Low': 'wo-pri-low' };
const ICON_CLASS    = { 'Open': 'wo-icon-red', 'In Progress': 'wo-icon-blue', 'Completed': 'wo-icon-green' };

const WO_CATEGORIES = [
  { id: 'Plumbing',   Icon: FaTint,            color: '#3b82f6' },
  { id: 'Electrical', Icon: FaBolt,            color: '#f97316' },
  { id: 'HVAC',       Icon: FaThermometerHalf, color: '#06b6d4' },
  { id: 'Appliance',  Icon: FaCog,             color: '#a855f7' },
  { id: 'Structural', Icon: FaBuilding,        color: '#d97706' },
  { id: 'Pest',       Icon: FaBug,             color: '#22c55e' },
  { id: 'Other',      Icon: FaQuestionCircle,  color: '#9ca3af' },
];

const WO_URGENCIES = [
  { id: 'Low',    sub: 'Can wait a few days',   Icon: FaMinus },
  { id: 'Medium', sub: 'Needs attention soon',  Icon: FaCircle },
  { id: 'Urgent', sub: 'Immediate attention',   Icon: FaExclamationTriangle },
];

const WO_STEP_LABELS = ['', "What's the issue?", 'Describe it', 'Final details'];

function WorkOrders() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [demoMode, setDemoMode] = useState(false);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [initials, setInitials] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [woStep, setWoStep] = useState(1);
  const [properties, setProperties] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [newWO, setNewWO] = useState({ title: '', description: '', propertyId: '', doorId: '', priority: 'Medium', dueDate: '', category: '', entryRequest: false, entryDate: '' });
  const [newWOPhotos, setNewWOPhotos] = useState([]); // File objects for new WO
  const [tenantMap, setTenantMap] = useState({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filterPriority, setFilterPriority] = useState([]);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterProperty, setFilterProperty] = useState('');

  const toggleArr = (arr, val) => arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  // Vendor pool management
  const [vendors, setVendors] = useState([]);
  const [showVendorPanel, setShowVendorPanel] = useState(false);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [vendorForm, setVendorForm] = useState({ trade: '', full_name: '', phone_number: '', details: '' });
  const [vendorSubmitting, setVendorSubmitting] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      try {
        const nameRes = await fetch(`${BASE_URL}/PropertyManagerName?email=${encodeURIComponent(user.email)}`);
        const nameData = await nameRes.json();
        const parts = nameData.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setUserEmail(user.email);
        await Promise.all([
          loadWorkOrders(user.email),
          loadProperties(user.email, nameData.Full_Name),
          loadVendors(user.email),
        ]);
        if (localStorage.getItem(`isDemoWorkOrders_${user.email}`) === 'true') {
          setWorkOrders(prev => prev.length === 0 ? [{
            ID: 'demo-wo-1',
            Title: 'Leaky Faucet — Bathroom',
            Priority: 'Low',
            Description: 'Sample work order to show how maintenance requests look in the system.',
            Unit_Number: '',
            Property_Name: 'Sample Property ✦',
            Date_Submitted: new Date().toISOString(),
            Due_Date: '',
            Status: 'Open',
            Attached_Expenses: [],
          }] : prev);
          setDemoMode(true);
          localStorage.removeItem(`hasSeenWorkOrdersTutorial_${user.email}`);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate]);

  // Fetch tenant info whenever the effective door changes inside the modal
  useEffect(() => {
    if (!newWO.propertyId) { setTenantInfo(null); return; }

    const propsForSelected = properties.filter(p => String(p.Property_ID) === String(newWO.propertyId));
    const isMulti = propsForSelected.length > 1;
    const doorId = isMulti ? newWO.doorId : propsForSelected[0]?.Door_id;

    if (!doorId) { setTenantInfo(null); return; }

    let cancelled = false;
    setTenantLoading(true);
    setTenantInfo(null);

    fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(doorId)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled) return;
        const tenant = data?.tenants?.[0] || null;
        const name = tenant?.Full_Name || tenant?.full_name || '';
        const phone = tenant?.Phone_Number || tenant?.phone_number || tenant?.Phone || '';
        if (name) {
          setTenantInfo({ name, phone });
        } else {
          setTenantInfo({ vacant: true });
        }
      })
      .catch(() => { if (!cancelled) setTenantInfo({ vacant: true }); })
      .finally(() => { if (!cancelled) setTenantLoading(false); });

    return () => { cancelled = true; };
  }, [newWO.propertyId, newWO.doorId, properties]);

  // After both workOrders + properties load, fetch tenant names keyed by work order ID
  useEffect(() => {
    if (!workOrders.length) return;
    const pairs = workOrders.map(wo => {
      const woId = String(wo.ID || wo.Work_Order_ID);
      const doorId = wo.Door_id || (() => {
        const match = properties.find(p =>
          p.Property_Name === wo.Property_Name &&
          String(p.Unit_Number || '') === String(wo.Unit_Number || '')
        ) || properties.find(p => p.Property_Name === wo.Property_Name);
        return match?.Door_id || '';
      })();
      return { woId, doorId };
    }).filter(x => x.doorId);

    if (!pairs.length) return;
    const uniqueDoors = [...new Set(pairs.map(p => p.doorId))];
    const doorToName = {};
    Promise.allSettled(uniqueDoors.map(async (doorId) => {
      try {
        const r = await fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(doorId)}`);
        if (r.ok) {
          const d = await r.json();
          const t = d?.tenants?.[0] || d?.tenant || null;
          const name = t?.Full_Name || t?.full_name || '';
          if (name) doorToName[String(doorId)] = name;
        }
      } catch {}
    })).then(() => {
      const map = {};
      pairs.forEach(({ woId, doorId }) => {
        if (doorToName[String(doorId)]) map[woId] = doorToName[String(doorId)];
      });
      setTenantMap(map);
    });
  }, [workOrders, properties]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadWorkOrders = async (email) => {
    try {
      const res = await fetch(
        `${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(email)}`
      );

      if (res.ok) {
        const data = await res.json();
        // Map API response to your UI structure
        const formatted = (data.work_orders || []).map((wo) => ({
          ID: wo.id,
          Title: wo.Title,
          Priority:
            wo.Priority === 3 || wo.Priority === '3'
              ? 'Urgent'
              : wo.Priority === 2 || wo.Priority === '2'
              ? 'Medium'
              : 'Low',
          Description: wo.Details,
          Unit_Number: wo.Unit_Number,
          Property_Name: wo.Property_Name,
          Date_Submitted: wo.Create_Date,
          Due_Date: wo.Due_Date,
          Status: wo.Stat,
          Attached_Expenses: wo.Attached_Expenses,
          Door_id: wo.door_id || wo.Door_id || wo.Door_ID || '',
          Category: wo.Category || wo.category || '',
          Entry_Request_Date: wo.entry_request_date || '',
          Entry_Request_Status: wo.entry_request_status || '',
        }));

        setWorkOrders(formatted);
      }
    } catch (err) {
      console.error('Failed to load work orders:', err);
    }
  };

  const loadProperties = async (email, fullName) => {
    try {
      const res = await fetch(`${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(fullName)}&Email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setProperties((data.Properties || []).sort((a, b) => (a.Property_Name || '').localeCompare(b.Property_Name || '')));
      }
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  };

  const loadVendors = async (email) => {
    try {
      const res = await fetch(`${BASE_URL}/GetServiceProsByPM?pm_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setVendors(data.service_pros || []);
      }
    } catch (err) {
      console.error('Vendors fetch failed:', err);
    }
  };

  const handleAddVendorGlobal = async () => {
    if (!vendorForm.trade || !vendorForm.full_name.trim()) return;
    setVendorSubmitting(true);
    const user = auth.currentUser;
    try {
      const res = await fetch(
        `${BASE_URL}/AddServicePro?full_name=${encodeURIComponent(vendorForm.full_name)}&phone_number=${encodeURIComponent(vendorForm.phone_number)}&trade=${encodeURIComponent(vendorForm.trade)}&details=${encodeURIComponent(vendorForm.details)}&pm_email=${encodeURIComponent(user.email)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        await loadVendors(user.email);
        setShowAddVendorModal(false);
        setVendorForm({ trade: '', full_name: '', phone_number: '', details: '' });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setVendorSubmitting(false);
    }
  };

  const handleAddWorkOrder = async () => {
    const propsForSelected = properties.filter(p => String(p.Property_ID) === String(newWO.propertyId));
    const isMulti = propsForSelected.length > 1;
    const effectiveDoorId = isMulti ? newWO.doorId : propsForSelected[0]?.Door_id;

    if (!newWO.title.trim() || !newWO.propertyId || !effectiveDoorId || !newWO.description.trim()) {
      showToast('Please fill all required fields (title, property, and description are required)', 'warning');
      return;
    }

    try {
      let priorityValue = '2';
      if (newWO.priority === 'Urgent') priorityValue = '3';
      if (newWO.priority === 'Low') priorityValue = '1';

      const formData = new FormData();
      formData.append('title', newWO.title.trim());
      formData.append('priority', priorityValue);
      formData.append('details', newWO.description.trim());
      formData.append('door_id', effectiveDoorId);
      if (newWO.category) formData.append('Category', newWO.category);
      if (newWO.dueDate) formData.append('due_date', newWO.dueDate);
      if (newWO.entryRequest) {
        if (!newWO.entryDate) {
          showToast('Please select a date for the entry request.', 'warning');
          return;
        }
        formData.append('entry_request_date', newWO.entryDate);
        formData.append('entry_request_status', 'Pending');
      }
      newWOPhotos.forEach((file, i) => {
        formData.append('photos', file, `photo_${i}.jpg`);
      });
      formData.append('CreatedBy', 'Manager');

      const res = await fetch(`${BASE_URL}/AddWorkOrder`, { method: 'POST', body: formData });

      if (!res.ok) {
        throw new Error('Failed to create work order');
      }

      const user = auth.currentUser;

      // API returns no ID — snapshot existing IDs before reload so we can find the new one
      const existingIds = new Set(workOrders.map(w => String(w.ID)));
      await loadWorkOrders(user.email);

      // After reload, workOrders state is stale here; re-fetch to find the new ID
      const freshRes = await fetch(`${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(user.email)}`);
      if (freshRes.ok) {
        const freshData = await freshRes.json();
        const newEntry = (freshData.work_orders || []).find(w => !existingIds.has(String(w.id)));
        if (newEntry?.id) {
          await fetch(
            `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Work Order Created')}&updated_by=${encodeURIComponent(user?.email || 'System')}&work_order_id=${newEntry.id}`,
            { method: 'POST' }
          );
          if (newWO.entryRequest) {
            await fetch(
              `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Entry requested by manager')}&updated_by=${encodeURIComponent(user?.email || 'System')}&work_order_id=${newEntry.id}`,
              { method: 'POST' }
            );
          }
        }
      }

      setShowNewModal(false);
      setNewWO({ title: '', description: '', propertyId: '', doorId: '', priority: 'Medium', dueDate: '', category: '', entryRequest: false, entryDate: '' });
      setNewWOPhotos([]);

      setTenantInfo(null);
    } catch (err) {
      console.error(err);
      showToast('Failed to create work order', 'error');
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')}/${dt.getFullYear()}`;
  };

  const filtered = workOrders.filter(w => {
    if (filter !== 'All' && w.Status !== filter) return false;
    if (filterPriority.length && !filterPriority.includes(w.Priority)) return false;
    if (filterCategory.length && !filterCategory.includes(w.Category)) return false;
    if (filterProperty && w.Property_Name !== filterProperty) return false;
    return true;
  });

  const activeFilterCount = filterPriority.length + filterCategory.length + (filterProperty ? 1 : 0);

  const counts = {
    open:       workOrders.filter(w => w.Status === 'Open').length,
    inProgress: workOrders.filter(w => w.Status === 'In Progress').length,
    completed:  workOrders.filter(w => w.Status === 'Completed').length,
  };

  const uniqueProperties = [...new Map(properties.map(p => [p.Property_ID, p])).values()];
  const unitsForProperty = properties.filter(p => String(p.Property_ID) === String(newWO.propertyId));
  const hasMultipleUnits = unitsForProperty.length > 1;

  if (loading) return <LoadingScreen />;

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Work Orders" />
        <div className="wo-container">

          {demoMode && (
            <div className="demo-mode-banner">
              📊 Sample data is shown below. Complete or skip the tutorial to remove it.
            </div>
          )}

          {/* Header */}
          <div className="wo-header-card">
            <div className="wo-header-left">
              <h1 className="wo-title">Work Orders</h1>
              <p className="wo-subtitle">Manage and track all work orders</p>
            </div>
            <div id="wo-stats" className="wo-header-stats">
              <div className="wo-stat-pill wo-stat-open">
                <div className="wo-stat-pill-icon"><FaExclamationTriangle /></div>
                <div className="wo-stat-pill-info">
                  <span className="wo-stat-pill-label">OPEN</span>
                  <span className="wo-stat-pill-num">{counts.open}</span>
                </div>
              </div>
              <div className="wo-stat-pill wo-stat-inprog">
                <div className="wo-stat-pill-icon"><FaClock /></div>
                <div className="wo-stat-pill-info">
                  <span className="wo-stat-pill-label">IN PROGRESS</span>
                  <span className="wo-stat-pill-num">{counts.inProgress}</span>
                </div>
              </div>
              <div className="wo-stat-pill wo-stat-done">
                <div className="wo-stat-pill-icon"><FaCheckCircle /></div>
                <div className="wo-stat-pill-info">
                  <span className="wo-stat-pill-label">COMPLETED</span>
                  <span className="wo-stat-pill-num">{counts.completed}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action bar */}
          <div id="wo-action-bar" className="wo-action-bar">
            <div className="wo-filter-tabs">
              {['All', 'Open', 'In Progress', 'Completed'].map(f => (
                <button key={f} className={`wo-tab ${filter === f ? 'wo-tab-active' : ''}`} onClick={() => setFilter(f)}>{f}</button>
              ))}
            </div>
            <div className="wo-action-btns">
              <button className="wo-vendors-btn" onClick={() => setShowVendorPanel(true)}>
                <FaTools /> Vendors
              </button>
              <button className="wo-add-btn" onClick={() => { setShowNewModal(true); setWoStep(1); setTenantInfo(null); setNewWOPhotos([]); setNewWO({ title: '', description: '', propertyId: '', doorId: '', priority: 'Medium', dueDate: '', category: '', entryRequest: false, entryDate: '' }); }}>
                <FaPlus /> New Work Order
              </button>
              <button className="wo-vendors-btn wo-filter-btn" onClick={() => setShowFilterPanel(true)}>
                <FaFilter /> Filters
                {activeFilterCount > 0 && <span className="wo-filter-badge">{activeFilterCount}</span>}
              </button>
            </div>
          </div>

          {/* Cards */}
          {filtered.length === 0 ? (
            <div className="wo-empty">
              <div className="wo-empty-icon"><FaWrench /></div>
              <h3>No work orders found</h3>
              <p>Create a new work order to get started.</p>
            </div>
          ) : (
            <div id="wo-list" className="wo-grid">
              {filtered.map(wo => {
                const id         = wo.ID || wo.Work_Order_ID;
                const title      = wo.Title || wo.Work_Order_Title || 'Untitled';
                const status     = wo.Status || 'Open';
                const isLate     = wo.Due_Date && new Date(wo.Due_Date) < new Date() && status !== 'Completed';
                const tenantName = tenantMap[String(id)] || '';
                return (
                  <div key={id} className="wo-card" onClick={() => navigate(`/work-orders/${id}`, { state: { workOrder: wo } })}>
                    <div className="wo-card-header">
                      <div className="wo-card-badges">
                        <span className={`wo-badge ${STATUS_CLASS[status] || 'wo-st-open'}`}>{status}</span>
                        {wo.Category && <span className="wo-category-pill"><FaTag className="wo-category-pill-icon" />{wo.Category}</span>}
                      </div>
                      <span className="wo-card-date">{fmtDate(wo.Date_Submitted || wo.Submitted)}</span>
                    </div>
                    <h3 className="wo-card-title">{title}</h3>
                    <div className="wo-card-meta">
                      <div className="wo-card-meta-row">
                        <FaBuilding className="wo-card-meta-icon" />
                        <span>{wo.Property_Name || 'Unknown Property'}</span>
                      </div>
                      {tenantName ? (
                        <div className="wo-card-meta-row">
                          <FaUser className="wo-card-meta-icon" />
                          <span>{tenantName}</span>
                        </div>
                      ) : null}
                      {wo.Due_Date && (
                        <div className={`wo-card-meta-row${isLate ? ' wo-card-due-late' : ''}`}>
                          <FaCalendarAlt className="wo-card-meta-icon" style={isLate ? { color: '#dc2626' } : {}} />
                          <span>Due {fmtDate(wo.Due_Date)}</span>
                        </div>
                      )}
                    </div>
                    <div className="wo-card-view-btn">
                      VIEW DETAILS <FaChevronRight />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── New Work Order — 3-step wizard ── */}
          {showNewModal && (
            <div className="tm-overlay" onClick={() => setShowNewModal(false)}>
              <div className="tm-modal tm-modal-new" onClick={e => e.stopPropagation()}>

                {/* Step header */}
                <div className="tm-step-hd">
                  <div className="tm-step-hd-top">
                    <div>
                      <p className="tm-step-label">STEP {woStep} OF 3</p>
                      <h2 className="tm-step-title">{WO_STEP_LABELS[woStep]}</h2>
                    </div>
                    <button className="tm-modal-close" onClick={() => setShowNewModal(false)}><FaTimes /></button>
                  </div>
                  <div className="tm-progress-track">
                    <div className="tm-progress-fill" style={{ width: `${(woStep / 3) * 100}%` }} />
                  </div>
                </div>

                {/* ── Step 1: Category grid ── */}
                {woStep === 1 && (
                  <div className="tm-modal-body-scroll">
                    <div className="tm-cat-grid">
                      {WO_CATEGORIES.map(({ id, Icon, color }) => (
                        <button
                          key={id}
                          className={`tm-cat-btn ${newWO.category === id ? 'tm-cat-selected' : ''}`}
                          onClick={() => { setNewWO(p => ({ ...p, category: id })); setWoStep(2); }}
                        >
                          <Icon style={{ fontSize: 28, color }} />
                          <span>{id}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Step 2: Title + Description + Photos ── */}
                {woStep === 2 && (
                  <>
                    <div className="tm-modal-body-scroll">
                      <div className="tm-new-form">
                        <label className="tm-field-lbl">TITLE <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                          className="tm-input"
                          placeholder="Brief summary of the issue"
                          value={newWO.title}
                          onChange={e => setNewWO(p => ({ ...p, title: e.target.value }))}
                          autoFocus
                        />
                        <div className="tm-desc-row">
                          <label className="tm-field-lbl">DESCRIPTION <span style={{ color: '#ef4444' }}>*</span></label>
                          <span className="tm-char-count">{newWO.description.length}/500</span>
                        </div>
                        <textarea
                          className="tm-input tm-textarea"
                          placeholder="Describe the issue in detail..."
                          maxLength={500}
                          value={newWO.description}
                          onChange={e => setNewWO(p => ({ ...p, description: e.target.value }))}
                        />

                        <label className="tm-field-lbl" style={{ marginTop: 10 }}>PHOTOS <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                        <div className="tm-photo-upload-area" onClick={() => photoInputRef.current?.click()}>
                          <FaImages style={{ fontSize: 22, color: '#9ca3af', marginBottom: 4 }} />
                          <span className="tm-photo-upload-text">Click to add photos</span>
                        </div>
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: 'none' }}
                          onChange={e => {
                            const files = Array.from(e.target.files || []);
                            setNewWOPhotos(prev => [...prev, ...files]);
                            e.target.value = '';
                          }}
                        />
                        {newWOPhotos.length > 0 && (
                          <div className="tm-photo-preview-row">
                            {newWOPhotos.map((file, i) => (
                              <div key={i} className="tm-photo-preview-wrap">
                                <img src={URL.createObjectURL(file)} alt="" className="tm-photo-preview-img" />
                                <button
                                  className="tm-photo-preview-remove"
                                  onClick={e => { e.stopPropagation(); setNewWOPhotos(prev => prev.filter((_, j) => j !== i)); }}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="tm-modal-footer tm-step-footer">
                      <button className="tm-back-btn" onClick={() => setWoStep(1)}>Back</button>
                      <button
                        className="tm-continue-btn"
                        onClick={() => setWoStep(3)}
                        disabled={!newWO.title.trim() || !newWO.description.trim()}
                      >Continue</button>
                    </div>
                  </>
                )}

                {/* ── Step 3: Priority, Property, Entry, Summary ── */}
                {woStep === 3 && (
                  <>
                    <div className="tm-modal-body-scroll">

                      <label className="tm-field-lbl">PRIORITY</label>
                      <div className="tm-urgency-grid">
                        {WO_URGENCIES.map(({ id, sub, Icon }) => (
                          <button
                            key={id}
                            className={`tm-urgency-btn ${newWO.priority === id ? 'tm-urgency-selected' : ''}`}
                            onClick={() => setNewWO(p => ({ ...p, priority: id }))}
                          >
                            <Icon style={{ fontSize: 18, marginBottom: 6, color: newWO.priority === id ? '#2563eb' : '#aaa' }} />
                            <span className="tm-urgency-label">{id}</span>
                            <span className="tm-urgency-sub">{sub}</span>
                          </button>
                        ))}
                      </div>

                      <label className="tm-field-lbl" style={{ marginTop: 12 }}>PROPERTY <span style={{ color: '#ef4444' }}>*</span></label>
                      <select
                        className="tm-input"
                        value={newWO.propertyId}
                        onChange={e => setNewWO(p => ({ ...p, propertyId: e.target.value, doorId: '' }))}
                      >
                        <option value="">Select property...</option>
                        {uniqueProperties.map(p => (
                          <option key={p.Property_ID} value={p.Property_ID}>
                            {p.Property_Name}{p.Street_Address ? ` — ${p.Street_Address}` : ''}
                          </option>
                        ))}
                      </select>

                      {newWO.propertyId && hasMultipleUnits && (
                        <>
                          <label className="tm-field-lbl">UNIT <span style={{ color: '#ef4444' }}>*</span></label>
                          <select
                            className="tm-input"
                            value={newWO.doorId}
                            onChange={e => setNewWO(p => ({ ...p, doorId: e.target.value }))}
                          >
                            <option value="">Select unit...</option>
                            {unitsForProperty.map(u => (
                              <option key={u.Door_id} value={u.Door_id}>
                                Unit {u.Unit_Number || u.Door_id}
                              </option>
                            ))}
                          </select>
                        </>
                      )}

                      {(tenantLoading || tenantInfo) && (
                        <div className="tm-wiz-tenant">
                          {tenantLoading ? (
                            <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading tenant…</span>
                          ) : tenantInfo?.vacant ? (
                            <div className="tm-wiz-tenant-row" style={{ color: '#d97706' }}>
                              <FaExclamationTriangle /><span>This unit is vacant — no tenant info required.</span>
                            </div>
                          ) : (
                            <>
                              <div className="tm-wiz-tenant-row"><FaUser style={{ color: '#2563eb' }} /><span>{tenantInfo?.name}</span></div>
                              {tenantInfo?.phone && <div className="tm-wiz-tenant-row"><FaPhone style={{ color: '#6b7280' }} /><span>{formatPhone(tenantInfo?.phone)}</span></div>}
                            </>
                          )}
                        </div>
                      )}

                      <label className="tm-field-lbl" style={{ marginTop: 8 }}>DUE DATE <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                      <input
                        type="date"
                        className="tm-input"
                        value={newWO.dueDate}
                        onChange={e => setNewWO(p => ({ ...p, dueDate: e.target.value }))}
                      />

                      <div className="tm-enter-row">
                        <div className="tm-enter-info">
                          <span className="tm-enter-title">Request entry if tenant isn't home</span>
                          <span className="tm-enter-sub">Allows a technician to access the unit without the tenant present.</span>
                        </div>
                        <label className="tm-toggle">
                          <input
                            type="checkbox"
                            checked={newWO.entryRequest}
                            onChange={e => setNewWO(p => ({ ...p, entryRequest: e.target.checked }))}
                          />
                          <span className="tm-toggle-track" />
                        </label>
                      </div>
                      {newWO.entryRequest && (
                        <>
                          <label className="tm-field-lbl">ENTRY DATE <span style={{ color: '#ef4444' }}>*</span></label>
                          <input
                            type="date"
                            className="tm-input"
                            value={newWO.entryDate}
                            min={new Date().toISOString().split('T')[0]}
                            onChange={e => setNewWO(p => ({ ...p, entryDate: e.target.value }))}
                          />
                        </>
                      )}

                      <label className="tm-field-lbl" style={{ marginTop: 8 }}>SUMMARY</label>
                      <div className="tm-summary-card">
                        <div className="tm-summary-icon">
                          {(() => { const c = WO_CATEGORIES.find(x => x.id === newWO.category); return c ? <c.Icon style={{ fontSize: 20, color: c.color }} /> : <FaWrench style={{ color: '#64748b' }} />; })()}
                        </div>
                        <div className="tm-summary-info">
                          <span className="tm-summary-title">{newWO.title || '—'}</span>
                          <span className="tm-summary-cat">{newWO.category || 'No category'}</span>
                        </div>
                        <span className={`tm-badge ${PRIORITY_CLASS[newWO.priority] || 'wo-pri-med'}`} style={{ fontSize: 11, padding: '3px 10px', flexShrink: 0 }}>
                          {newWO.priority}
                        </span>
                      </div>

                    </div>
                    <div className="tm-modal-footer tm-step-footer">
                      <button className="tm-back-btn" onClick={() => setWoStep(2)}>Back</button>
                      <button className="tm-submit-btn" onClick={handleAddWorkOrder}>
                        Create Work Order
                      </button>
                    </div>
                  </>
                )}

              </div>
            </div>
          )}

        </div>
      </div>

      {/* Vendor Panel (slide-out) */}
      {showVendorPanel && (
        <div className="modal-overlay" onClick={() => setShowVendorPanel(false)}>
          <div className="wo-vendor-panel" onClick={e => e.stopPropagation()}>
            <div className="wo-vendor-panel-hd">
              <h2><FaTools style={{ marginRight: 8 }} />Vendors</h2>
              <button className="modal-close-btn" onClick={() => setShowVendorPanel(false)}><FaTimes /></button>
            </div>
            <div className="wo-vendor-panel-body">
              {vendors.length === 0 ? (
                <div className="wo-vendor-empty">No vendors added yet.</div>
              ) : (
                <div className="wo-vendor-list">
                  {vendors.map((v, i) => (
                    <div key={v.id || i} className="wo-vendor-row">
                      <div className="wo-vendor-trade">{v.trade || v.Trade || '—'}</div>
                      <div className="wo-vendor-name">{v.full_name || v.Full_Name}</div>
                      {(v.phone_number || v.Phone_Number) && (
                        <div className="wo-vendor-phone">
                          <FaPhone style={{ marginRight: 5, fontSize: 11 }} />
                          {formatPhone(v.phone_number || v.Phone_Number)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="wo-vendor-panel-footer">
              <button className="wo-submit-btn" style={{ width: '100%' }} onClick={() => { setShowAddVendorModal(true); setVendorForm({ trade: '', full_name: '', phone_number: '', details: '' }); }}>
                <FaPlus style={{ marginRight: 6 }} /> Add New Vendor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Panel (slide-out from right) */}
      {showFilterPanel && (
        <div className="modal-overlay" onClick={() => setShowFilterPanel(false)}>
          <div className="wo-filter-panel" onClick={e => e.stopPropagation()}>
            <div className="wo-filter-panel-hd">
              <h2><FaFilter style={{ marginRight: 8, fontSize: 15 }} />Filters</h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {activeFilterCount > 0 && (
                  <button className="wo-filter-clear-btn" onClick={() => { setFilterPriority([]); setFilterCategory([]); setFilterProperty(''); }}>
                    Clear All
                  </button>
                )}
                <button className="modal-close-btn" onClick={() => setShowFilterPanel(false)}><FaTimes /></button>
              </div>
            </div>
            <div className="wo-filter-panel-body">

              {/* Priority */}
              <div className="wo-filter-section">
                <div className="wo-filter-section-title">Priority</div>
                {['Urgent', 'Medium', 'Low'].map(p => (
                  <label key={p} className="wo-filter-check-row">
                    <input type="checkbox" checked={filterPriority.includes(p)} onChange={() => setFilterPriority(prev => toggleArr(prev, p))} />
                    <span>{p}</span>
                  </label>
                ))}
              </div>

              {/* Category */}
              <div className="wo-filter-section">
                <div className="wo-filter-section-title">Category</div>
                {WO_CATEGORIES.map(({ id: c }) => (
                  <label key={c} className="wo-filter-check-row">
                    <input type="checkbox" checked={filterCategory.includes(c)} onChange={() => setFilterCategory(prev => toggleArr(prev, c))} />
                    <span>{c}</span>
                  </label>
                ))}
              </div>

              {/* Property */}
              <div className="wo-filter-section">
                <div className="wo-filter-section-title">Property</div>
                <select
                  className="wo-field-input"
                  value={filterProperty}
                  onChange={e => setFilterProperty(e.target.value)}
                >
                  <option value="">All Properties</option>
                  {[...new Set(workOrders.map(w => w.Property_Name).filter(Boolean))].sort().map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Add Vendor Modal */}
      {showAddVendorModal && (
        <div className="modal-overlay" onClick={() => setShowAddVendorModal(false)}>
          <div className="wo-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="wo-modal-header">
              <div>
                <h2>Add Vendor</h2>
                <p className="wo-modal-subtitle">Add a new vendor to your pool</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowAddVendorModal(false)}><FaTimes /></button>
            </div>
            <div className="wo-modal-body">
              <div className="wo-form-group wo-full">
                <label className="wo-field-label">Trade / Category *</label>
                <select className="wo-field-input" value={vendorForm.trade} onChange={e => setVendorForm(p => ({ ...p, trade: e.target.value }))}>
                  <option value="">Select trade...</option>
                  {['Plumbing','Electrical','HVAC','Carpentry','Painting','Landscaping','Roofing','General','Cleaning','Other'].map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="wo-form-group wo-full">
                <label className="wo-field-label">Full Name *</label>
                <input className="wo-field-input" placeholder="Vendor or company name" value={vendorForm.full_name} onChange={e => setVendorForm(p => ({ ...p, full_name: e.target.value }))} />
              </div>
              <div className="wo-form-group wo-full">
                <label className="wo-field-label">Phone</label>
                <input className="wo-field-input" placeholder="e.g. (555) 123-4567" value={vendorForm.phone_number} onChange={e => setVendorForm(p => ({ ...p, phone_number: formatPhone(e.target.value) }))} />
              </div>
              <div className="wo-form-group wo-full">
                <label className="wo-field-label">Notes</label>
                <textarea className="wo-field-textarea" placeholder="Additional details..." value={vendorForm.details} onChange={e => setVendorForm(p => ({ ...p, details: e.target.value }))} />
              </div>
            </div>
            <div className="wo-modal-footer">
              <button className="wo-cancel-btn" onClick={() => setShowAddVendorModal(false)}>Cancel</button>
              <button className="wo-submit-btn" onClick={handleAddVendorGlobal} disabled={vendorSubmitting}>
                {vendorSubmitting ? 'Adding...' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {userEmail && (
        <Tutorial
          steps={[
            { title: 'Welcome to Work Orders!', description: 'Track and manage every maintenance request across your entire portfolio — all in one place.', anchorId: null, cardAlignment: 'bottom' },
            { title: 'Status Overview', description: 'These pills show your live Open, In Progress, and Completed counts so you can see the overall workload at a glance.', anchorId: 'wo-stats' },
            { title: 'Work Order Cards', description: "Each card shows the status, property, assigned tenant, priority, and due date. Tap any card to view full details, update status, or resolve it.", anchorId: 'wo-list' },
            { title: 'Filter & Create', description: 'Switch between All, Open, In Progress, and Completed using the filter tabs. Tap "+ New Work Order" to log a new maintenance request — set the property, priority, and a description.', anchorId: 'wo-action-bar' },
          ]}
          storageKey={`hasSeenWorkOrdersTutorial_${userEmail}`}
          onStep={(stepIndex) => {
            if (stepIndex === -1) {
              localStorage.removeItem(`isDemoWorkOrders_${userEmail}`);
              setDemoMode(false);
              setWorkOrders(prev => prev.filter(wo => wo.ID !== 'demo-wo-1'));
            }
          }}
        />
      )}
    </div>
  );
}

export default WorkOrders;
